// A multi-select filter for Needed from Amazon's toolbar: a button reading the choice and
// a menu of ticks, drawn as the toolbar's selects (Noaa, 21 and 26 Oct 2026).
import { useCallback, useRef, useState } from "react";

import { useDismiss } from "../../utils/useDismiss";
import overview from "./amazonOverview.module.css";
import styles from "./openIssues.module.css";

export interface FilterOption<T extends string> {
  value: T;
  label: string;
  count?: number;
}

/** The button's words for a filter whose empty choice means every option. */
const pickedSummary = <T extends string>(
  options: FilterOption<T>[],
  picked: T[],
  all: string,
): string => {
  const labels = options.filter((o) => picked.includes(o.value)).map((o) => o.label);
  if (labels.length === 0) return all;
  return labels.length === 1 ? labels[0] : `${labels[0]} + ${labels.length - 1} more`;
};

export const FilterSelect = <T extends string>({
  name,
  summary,
  options,
  picked,
  onChange,
  all,
}: {
  /** The button's accessible name. */
  name: string;
  /** The button's words, where they are not the picked options' (``pickedSummary``). */
  summary?: string;
  options: FilterOption<T>[];
  picked: T[];
  onChange: (next: T[]) => void;
  /** A first row that clears the choice, where nothing picked means every option. */
  all?: string;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, ref);
  const words = summary ?? pickedSummary(options, picked, all ?? "");
  return (
    <div className={styles.filterRoot} ref={ref}>
      <button
        type="button"
        className={`${styles.select} ${styles.filterButton}`}
        aria-label={name}
        aria-haspopup="true"
        aria-expanded={open}
        title={words}
        onClick={() => setOpen(!open)}
      >
        <span className={styles.filterSummary}>{words}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M3 4.5 6 7.5 9 4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>
      {open && (
        <div className={styles.filterMenu}>
          {all !== undefined && (
            <label className={styles.filterOption}>
              <input
                type="checkbox"
                checked={picked.length === 0}
                onChange={() => onChange([])}
              />
              <span>{all}</span>
            </label>
          )}
          {options.map((o) => (
            <label key={o.value} className={styles.filterOption}>
              <input
                type="checkbox"
                checked={picked.includes(o.value)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? options
                          .map((x) => x.value)
                          .filter((x) => x === o.value || picked.includes(x))
                      : picked.filter((x) => x !== o.value),
                  )
                }
              />
              <span>{o.label}</span>
              {o.count !== undefined && <span className={overview.muted}>{o.count}</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
